import { createSelector } from 'reselect';

import { AnalysisSet, CheckResult } from '@paragon/contract-library';

import { isPassingNonLoadCapacityCheck } from '../../library/isPassingNonLoadCapacityCheck';
import { RootState } from '..';
import { getActiveAnalysisSet } from './getActiveAnalysisSetSelector';
import { getCheckResultsForActiveAnalysisSetByObjectGuid } from './getCheckResultsByObjectGuidSelectors';
import { getCheckResultsForActiveAnalysisSet } from './getCheckResultsForActiveAnalysisSetSelector';
import {
  getCheckedObjectGuidsAssociatedWithMember,
  getCheckResultsForMember,
} from './getCheckResultsForActiveLoadCaseAndItemSelector';

export function getHighestCapacityForItemUsingSummary(
  analysisSet: AnalysisSet,
  objectGuid: string,
  objectType: string,
) {
  const {
    componentDesign: { analogModel },
    objectCapacities,
  } = analysisSet;
  if (!analogModel || !objectCapacities) {
    return null;
  }
  if (objectType === 'member') {
    const associatedObjectCapacities = getCheckedObjectGuidsAssociatedWithMember(objectGuid, analogModel)
      .map((guid) => objectCapacities[guid]?.capacity)
      .filter((capacity) => capacity != null);
    return associatedObjectCapacities.length ? Math.max(...associatedObjectCapacities) : null;
  }
  return objectCapacities[objectGuid]?.capacity ?? null;
}

function getHighestCapacityForItem(
  checkResultsForAnalysisSet: readonly CheckResult[] | null,
  checkResultsByObjectGuid: ReadonlyMap<string, readonly CheckResult[]> | null,
  analysisSet: AnalysisSet | null,
  objectGuid: string,
  objectType: string,
) {
  if (!analysisSet) {
    return undefined;
  }
  if (!checkResultsForAnalysisSet) {
    return getHighestCapacityForItemUsingSummary(analysisSet, objectGuid, objectType) ?? undefined;
  }
  if (!checkResultsByObjectGuid) {
    return undefined;
  }

  const checkResultsForItem =
    objectType === 'member'
      ? getCheckResultsForMember(checkResultsByObjectGuid, objectGuid, analysisSet.analogModel)
      : checkResultsForAnalysisSet.filter((checkResult) =>
          checkResult.objects.some((objectUnderTest) => objectUnderTest.guid === objectGuid),
        );

  const capacitiesForColoringItem = checkResultsForItem
    .filter((checkResult) => !isPassingNonLoadCapacityCheck(checkResult))
    .map((checkResult) => checkResult.capacity);

  const highestCapacity = Math.max(...capacitiesForColoringItem);

  return highestCapacity || undefined;
}

const createCachedGetHighestCapacityForItem = createSelector(
  getCheckResultsForActiveAnalysisSet,
  getCheckResultsForActiveAnalysisSetByObjectGuid,
  getActiveAnalysisSet,
  (checkResultsForAnalysisSet, checkResultsByObjectGuid, analysisSet) => {
    const cache = new Map<string, number | undefined>();

    return function getHighestCapacityForItemCached(objectGuid: string, objectType: string) {
      if (!cache.has(objectGuid)) {
        cache.set(
          objectGuid,
          getHighestCapacityForItem(
            checkResultsForAnalysisSet,
            checkResultsByObjectGuid,
            analysisSet,
            objectGuid,
            objectType,
          ),
        );
      }

      return cache.get(objectGuid);
    };
  },
);

function getHighestCapacityForItemCached(state: RootState, objectGuid: string, objectType: string) {
  return createCachedGetHighestCapacityForItem(state)(objectGuid, objectType);
}

export { getHighestCapacityForItemCached as getHighestCapacityForItem };
